<template>
  <div class="platform-pay-page">
    <section class="page-toolbar">
      <div class="toolbar-copy">
        <strong>平台支付配置</strong>
        <span>会员小鼠订单统一使用平台微信支付账户收款，卖鼠商家只负责履约。</span>
      </div>
      <div class="toolbar-actions">
        <button class="plain-button" type="button" :disabled="loading" @click="loadConfig">
          {{ loading ? "加载中..." : "刷新" }}
        </button>
        <button class="primary-button" type="button" :disabled="saving" @click="saveConfig">
          {{ saving ? "保存中..." : "保存配置" }}
        </button>
      </div>
    </section>

    <section class="status-strip" :class="{ disabled: !config.enabled }">
      <div>
        <span class="status-dot"></span>
        <strong>{{ config.enabled ? "平台支付已启用" : "平台支付未启用" }}</strong>
        <span>{{ config.bound ? "支付账户已配置" : "尚未配置支付账户" }}</span>
      </div>
      <div>
        <span>适用业务</span>
        <strong>会员小鼠套餐</strong>
      </div>
      <div>
        <span>收款主体</span>
        <strong>平台统一账户</strong>
      </div>
      <div>
        <span>回调验签</span>
        <strong>{{ config.callbackVerificationMode === "PUBLIC_KEY" ? "微信支付公钥" : "平台证书" }}</strong>
      </div>
    </section>

    <div class="pay-layout">
      <section class="form-panel">
        <header>
          <div>
            <strong>微信支付 V3 · 小程序 JSAPI</strong>
            <span>敏感信息在服务端加密保存，页面不会回显密钥明文。</span>
          </div>
          <label class="enable-switch">
            <input v-model="form.status" type="checkbox">
            <span>启用支付</span>
          </label>
        </header>

        <form @submit.prevent="saveConfig">
          <label>
            <span>小程序 AppID</span>
            <input v-model.trim="form.appId" placeholder="微信小程序 AppID">
            <small v-if="config.miniAppAppId">服务端当前小程序：{{ config.miniAppAppId }}</small>
          </label>
          <label>
            <span>微信支付商户号 MchID</span>
            <input
              v-model.trim="form.mchId"
              inputmode="numeric"
              maxlength="32"
              autocomplete="off"
              placeholder="平台微信支付商户号，仅填写纯数字"
            >
          </label>
          <label>
            <span>商户证书序列号</span>
            <input v-model.trim="form.serialNo" placeholder="API 证书序列号">
          </label>
          <label>
            <span>APIv3 密钥</span>
            <input
              v-model="form.apiV3Key"
              type="password"
              :placeholder="config.apiV3KeyConfigured ? '已配置，留空保持不变' : '请输入 32 位 APIv3 密钥'"
            >
          </label>
          <label class="wide">
            <span>商户 API 私钥 PEM</span>
            <textarea
              v-model="form.privateKeyPem"
              :placeholder="config.privateKeyConfigured ? '已配置，留空保持不变' : '粘贴 apiclient_key.pem 内容'"
            ></textarea>
          </label>
          <label class="wide">
            <span>微信支付公钥 ID</span>
            <input
              v-model.trim="form.publicKeyId"
              maxlength="128"
              autocomplete="off"
              placeholder="例如：PUB_KEY_ID_..."
            >
            <small>微信支付公钥模式下，回调请求头 Wechatpay-Serial 会使用该公钥 ID。</small>
          </label>
          <label class="wide">
            <span>微信支付公钥 PEM</span>
            <textarea
              v-model="form.publicKeyPem"
              :placeholder="config.publicKeyConfigured ? '已配置，留空保持不变' : '粘贴商户平台下载的微信支付公钥内容'"
            ></textarea>
            <small>公钥 ID 与公钥 PEM 必须同时配置；配置后优先使用公钥验签。</small>
          </label>
          <label class="wide">
            <span>微信支付平台证书 PEM（证书模式备用）</span>
            <textarea
              v-model="form.certificatePem"
              :placeholder="config.certificateConfigured ? '已配置，留空保持不变' : '仅证书验签模式需要，粘贴微信支付平台证书内容'"
            ></textarea>
          </label>
          <div class="form-actions">
            <button class="primary-button" type="submit" :disabled="saving">
              {{ saving ? "保存中..." : "保存平台支付配置" }}
            </button>
          </div>
        </form>
      </section>

      <aside class="info-column">
        <section class="info-card">
          <strong>支付回调地址</strong>
          <p>需在微信支付商户平台中允许访问，并由公网 HTTPS 域名接收。</p>
          <code>{{ config.callbackUrl || "-" }}</code>
          <button type="button" @click="copyCallback">复制地址</button>
        </section>

        <section class="info-card">
          <strong>配置说明</strong>
          <ul>
            <li>会员套餐、小鼠订单均由平台账户统一收款。</li>
            <li>卖鼠商家账号只接收履约订单，不配置收款账户。</li>
            <li>小程序使用 JSAPI 支付，支付结果以微信服务器回调为准。</li>
            <li>公钥验签适合“微信支付公钥”模式；平台证书验签继续兼容。</li>
            <li>回调会校验签名、AppID、商户号、用户 OpenID 和支付金额。</li>
          </ul>
        </section>

        <section class="info-card warning-card">
          <strong>上线前检查</strong>
          <p>必须完成商户号绑定、JSAPI 支付授权、商户 API 私钥和回调验签材料配置。当前商户号如提示“无可用的平台证书”，请配置微信支付公钥及公钥 ID。</p>
        </section>
      </aside>
    </div>

    <div v-if="toast" class="toast" :class="{ 'toast--error': toastError }">{{ toast }}</div>
  </div>
</template>

<script>
import { fetchPlatformPayConfig, updatePlatformPayConfig } from "../api/adminSystem"

export default {
  name: "PlatformPayView",
  data() {
    return {
      loading: false,
      saving: false,
      config: {},
      form: {
        appId: "",
        mchId: "",
        serialNo: "",
        apiV3Key: "",
        privateKeyPem: "",
        publicKeyId: "",
        publicKeyPem: "",
        certificatePem: "",
        status: true
      },
      toast: "",
      toastError: false,
      toastTimer: null
    }
  },
  mounted() {
    this.loadConfig()
  },
  beforeDestroy() {
    clearTimeout(this.toastTimer)
  },
  methods: {
    async loadConfig() {
      this.loading = true
      try {
        const response = await fetchPlatformPayConfig()
        this.config = response.data || {}
        this.form = {
          appId: this.config.appId || this.config.miniAppAppId || "",
          mchId: this.config.mchId || "",
          serialNo: this.config.serialNo || "",
          apiV3Key: "",
          privateKeyPem: "",
          publicKeyId: this.config.publicKeyId || "",
          publicKeyPem: "",
          certificatePem: "",
          status: this.config.enabled !== false
        }
      } catch (error) {
        this.showToast(error.message || "平台支付配置读取失败", true)
      } finally {
        this.loading = false
      }
    },
    async saveConfig() {
      if (this.saving) return
      if (!/^\d{6,32}$/.test(this.form.mchId)) {
        this.showToast("微信支付商户号应为 6-32 位纯数字，请检查是否误填了密钥或证书", true)
        return
      }
      this.saving = true
      try {
        const response = await updatePlatformPayConfig({
          ...this.form,
          status: this.form.status ? 1 : 0
        })
        this.config = response.data || {}
        this.form.apiV3Key = ""
        this.form.privateKeyPem = ""
        this.form.publicKeyPem = ""
        this.form.certificatePem = ""
        this.showToast("平台支付配置已保存")
      } catch (error) {
        this.showToast(error.message || "平台支付配置保存失败", true)
      } finally {
        this.saving = false
      }
    },
    async copyCallback() {
      if (!this.config.callbackUrl) return
      try {
        await navigator.clipboard.writeText(this.config.callbackUrl)
        this.showToast("回调地址已复制")
      } catch (error) {
        this.showToast("复制失败，请手动选择地址", true)
      }
    },
    showToast(message, error = false) {
      clearTimeout(this.toastTimer)
      this.toast = message
      this.toastError = error
      this.toastTimer = setTimeout(() => {
        this.toast = ""
      }, 3200)
    }
  }
}
</script>

<style scoped>
.platform-pay-page {
  max-width: 1280px;
  color: #26323e;
}

.page-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 16px;
}

.toolbar-copy strong,
.toolbar-copy span {
  display: block;
}

.toolbar-copy strong {
  font-size: 18px;
}

.toolbar-copy span {
  margin-top: 6px;
  color: #7b8791;
  font-size: 10px;
}

.toolbar-actions {
  display: flex;
  gap: 8px;
}

.status-strip {
  display: grid;
  min-height: 64px;
  margin-bottom: 14px;
  padding: 0 18px;
  align-items: center;
  grid-template-columns: 1.5fr 0.8fr 0.8fr 0.8fr;
  gap: 18px;
  border: 1px solid #cfe7db;
  border-radius: 6px;
  background: #f4fbf7;
}

.status-strip.disabled {
  border-color: #ead7b6;
  background: #fffaf0;
}

.status-strip > div {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-strip span {
  color: #7a8780;
  font-size: 9px;
}

.status-strip strong {
  color: #30443a;
  font-size: 10px;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #20a366;
}

.status-strip.disabled .status-dot {
  background: #d49331;
}

.pay-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(300px, 0.65fr);
  gap: 14px;
}

.form-panel,
.info-card {
  border: 1px solid #e1e6ea;
  border-radius: 6px;
  background: #fff;
}

.form-panel > header {
  display: flex;
  min-height: 64px;
  padding: 0 18px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid #edf0f2;
}

.form-panel header strong,
.form-panel header span {
  display: block;
}

.form-panel header strong {
  font-size: 12px;
}

.form-panel header span {
  margin-top: 5px;
  color: #8b959d;
  font-size: 9px;
}

.enable-switch {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #55636d;
  font-size: 10px;
  white-space: nowrap;
}

.form-panel form {
  display: grid;
  padding: 18px;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-panel form label {
  display: block;
}

.form-panel form label.wide,
.form-actions {
  grid-column: 1 / -1;
}

.form-panel label > span {
  display: block;
  margin-bottom: 7px;
  color: #53616b;
  font-size: 10px;
  font-weight: 600;
}

.form-panel input:not([type="checkbox"]),
.form-panel textarea {
  width: 100%;
  padding: 0 11px;
  border: 1px solid #d2dae0;
  border-radius: 4px;
  outline: none;
  color: #26323e;
  background: #fff;
  font-size: 11px;
}

.form-panel input:not([type="checkbox"]) {
  height: 38px;
}

.form-panel textarea {
  min-height: 100px;
  padding-top: 10px;
  resize: vertical;
  line-height: 1.5;
}

.form-panel input:focus,
.form-panel textarea:focus {
  border-color: #7fa9d8;
  box-shadow: 0 0 0 3px rgba(59, 130, 196, 0.08);
}

.form-panel small {
  display: block;
  margin-top: 6px;
  color: #929da6;
  font-size: 8px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.info-column {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.info-card {
  padding: 16px;
}

.info-card > strong {
  color: #34424c;
  font-size: 11px;
}

.info-card p {
  margin: 8px 0 0;
  color: #77838c;
  font-size: 9px;
  line-height: 1.7;
}

.info-card code {
  display: block;
  margin-top: 10px;
  padding: 9px 10px;
  overflow-wrap: anywhere;
  border-radius: 4px;
  color: #315f8d;
  background: #f3f7fb;
  font-size: 8px;
}

.info-card button {
  height: 28px;
  margin-top: 9px;
  padding: 0 9px;
  border: 1px solid #d1d9df;
  border-radius: 4px;
  color: #55636d;
  background: #fff;
  font-size: 8px;
}

.info-card ul {
  margin: 10px 0 0;
  padding-left: 16px;
  color: #6f7b84;
  font-size: 9px;
  line-height: 1.8;
}

.warning-card {
  border-color: #f0dfc4;
  background: #fffbf4;
}

.toast {
  position: fixed;
  z-index: 80;
  right: 24px;
  bottom: 24px;
  padding: 11px 15px;
  border-radius: 4px;
  color: #fff;
  background: #26323e;
  font-size: 10px;
}

.toast--error {
  background: #a43e3e;
}

@media (max-width: 980px) {
  .pay-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 680px) {
  .page-toolbar,
  .status-strip {
    align-items: flex-start;
    flex-direction: column;
  }

  .status-strip {
    display: flex;
    padding: 14px 16px;
    gap: 10px;
  }

  .form-panel form {
    grid-template-columns: 1fr;
  }

  .form-panel form label.wide,
  .form-actions {
    grid-column: auto;
  }
}
</style>
